import { useState, useEffect, useRef, useCallback } from 'react';
import { openNavigationForArea } from '@/utils/navigationUtils';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { AlertDialog, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogAction, AlertDialogCancel } from '@/components/ui/alert-dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Plus, MapPin, ArrowLeft, Trash2, Check, X, Edit, Loader2, Map as MapIcon } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import type { D2DStreet, D2DCreateStreetData } from '@/types/d2d';
import { PresenceIndicator } from './PresenceIndicator';
import type { UserPresence } from '@/hooks/useAreaPresence';

// Constants for state persistence
const STREET_LIST_SCROLL_KEY = 'street-list-scroll-position';
const STREET_LIST_STATE_KEY = 'street-list-state';

interface D2DStreetListProps {
  streets: D2DStreet[];
  loading: boolean;
  lastPlz?: string;
  area?: { name: string; plz?: string; is_active?: boolean } | null;
  onSelectStreet: (street: D2DStreet) => void;
  onCreateStreet: (streetData: D2DCreateStreetData) => Promise<void>;
  onDeleteStreet?: (streetId: string) => Promise<void>;
  onEditStreetName?: (streetId: string, newName: string) => Promise<void>;
  onBack: () => void;
  getUsersInStreet?: (streetId: string) => UserPresence[];
}

interface StreetSuggestion {
  display_name: string;
  name: string;
  postcode?: string;
  city?: string;
  state?: string;
}

interface SwipeableStreetCardProps {
  street: D2DStreet;
  onSelect: () => void;
  onDelete?: () => void;
  onEdit?: () => void;
  showDelete: boolean;
  isAreaInactive?: boolean;
  usersInStreet?: UserPresence[];
}

const SwipeableStreetCard = ({ street, onSelect, onDelete, onEdit, showDelete, isAreaInactive, usersInStreet = [] }: SwipeableStreetCardProps) => {
  const cardRef = useRef<HTMLDivElement>(null);
  const backgroundRef = useRef<HTMLDivElement>(null);
  const startXRef = useRef(0);
  const startYRef = useRef(0);
  const currentXRef = useRef(0);
  const isDraggingRef = useRef(false);
  const isSwipingRef = useRef(false);
  const animationFrameRef = useRef<number>();
  const longPressTimerRef = useRef<NodeJS.Timeout>();
  const longPressTriggeredRef = useRef(false);

  const updateTransform = useCallback((x: number) => {
    if (cardRef.current && backgroundRef.current) {
      const clampedX = Math.min(0, Math.max(-80, x));
      cardRef.current.style.transform = `translateX(${clampedX}px)`;
      
      const opacity = Math.abs(clampedX) / 80;
      backgroundRef.current.style.opacity = opacity.toString();
    }
  }, []);

  const resetPosition = useCallback(() => {
    if (cardRef.current && backgroundRef.current) {
      cardRef.current.style.transform = 'translateX(0px)';
      cardRef.current.style.transition = 'transform 0.3s cubic-bezier(0.4, 0, 0.2, 1)';
      backgroundRef.current.style.opacity = '0';
      
      setTimeout(() => {
        if (cardRef.current) {
          cardRef.current.style.transition = '';
        }
      }, 300);
    }
  }, []);

  const startLongPress = useCallback(() => {
    longPressTriggeredRef.current = false;
    longPressTimerRef.current = setTimeout(() => {
      if (!isSwipingRef.current && onEdit) {
        longPressTriggeredRef.current = true;
        onEdit();
      }
    }, 500); // 500ms for long press
  }, [onEdit]);

  const clearLongPress = useCallback(() => {
    if (longPressTimerRef.current) {
      clearTimeout(longPressTimerRef.current);
      longPressTimerRef.current = undefined;
    }
    longPressTriggeredRef.current = false;
  }, []);

  const handleStart = useCallback((clientX: number, clientY: number) => {
    if (!showDelete) return;
    
    startXRef.current = clientX;
    startYRef.current = clientY;
    currentXRef.current = 0;
    isDraggingRef.current = true;
    isSwipingRef.current = false;
    
    startLongPress();
  }, [showDelete, startLongPress]);

  const handleMove = useCallback((clientX: number, clientY: number) => {
    if (!isDraggingRef.current || !showDelete) return;
    
    const deltaX = clientX - startXRef.current;
    const deltaY = clientY - startYRef.current;
    
    if (!isSwipingRef.current) {
      const minSwipeDistance = 15;
      const maxVerticalDeviation = 30;
      
      if (Math.abs(deltaX) < minSwipeDistance) return;
      if (Math.abs(deltaY) > maxVerticalDeviation) return;
      if (deltaX > 0) return;
      
      isSwipingRef.current = true;
      clearLongPress(); // Cancel long press when swiping
      if (cardRef.current) {
        cardRef.current.style.transition = '';
      }
    }
    
    if (isSwipingRef.current) {
      currentXRef.current = deltaX;
      
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }
      
      animationFrameRef.current = requestAnimationFrame(() => {
        updateTransform(deltaX);
      });
    }
  }, [updateTransform, showDelete, clearLongPress]);

  const handleEnd = useCallback(() => {
    if (!isDraggingRef.current || !showDelete) return;
    
    isDraggingRef.current = false;
    const wasSwipping = isSwipingRef.current;
    isSwipingRef.current = false;
    clearLongPress();
    
    if (animationFrameRef.current) {
      cancelAnimationFrame(animationFrameRef.current);
    }
    
    if (wasSwipping && currentXRef.current < -40 && onDelete) {
      onDelete();
    }
    
    resetPosition();
  }, [resetPosition, onDelete, showDelete, clearLongPress]);

  const handleMouseDown = useCallback((e: React.MouseEvent) => {
    handleStart(e.clientX, e.clientY);
  }, [handleStart]);

  const handleMouseMove = useCallback((e: React.MouseEvent) => {
    handleMove(e.clientX, e.clientY);
  }, [handleMove]);

  const handleMouseUp = useCallback(() => {
    handleEnd();
  }, [handleEnd]);

  const handleMouseLeave = useCallback(() => {
    if (isDraggingRef.current) {
      handleEnd();
    }
  }, [handleEnd]);

  const handleTouchStart = useCallback((e: React.TouchEvent) => {
    const touch = e.touches[0];
    if (touch) {
      handleStart(touch.clientX, touch.clientY);
    }
  }, [handleStart]);

  const handleTouchMove = useCallback((e: React.TouchEvent) => {
    const touch = e.touches[0];
    if (touch) {
      handleMove(touch.clientX, touch.clientY);
    }
  }, [handleMove]);

  const handleTouchEnd = useCallback(() => {
    handleEnd();
  }, [handleEnd]);

  const handleCardClick = useCallback((e: React.MouseEvent) => {
    if (isDraggingRef.current || longPressTriggeredRef.current || isSwipingRef.current) {
      e.preventDefault();
      e.stopPropagation();
      return;
    }
    onSelect();
  }, [onSelect]);

  return (
    <div className="relative overflow-hidden rounded-lg">
      {showDelete && (
        <div 
          ref={backgroundRef}
          className="absolute inset-0 bg-red-500 flex items-center justify-end pr-6 opacity-0"
        >
          <Trash2 className="h-6 w-6 text-white" />
        </div>
      )}
      
      <Card 
        ref={cardRef}
        className={`p-4 cursor-pointer hover:shadow-md transition-colors relative z-10 ${isAreaInactive ? 'opacity-50 grayscale' : ''}`}
        onClick={handleCardClick}
        onMouseDown={showDelete ? handleMouseDown : undefined}
        onMouseMove={showDelete ? handleMouseMove : undefined}
        onMouseUp={showDelete ? handleMouseUp : undefined}
        onMouseLeave={showDelete ? handleMouseLeave : undefined}
        onTouchStart={showDelete ? handleTouchStart : undefined}
        onTouchMove={showDelete ? handleTouchMove : undefined}
        onTouchEnd={showDelete ? handleTouchEnd : undefined}
        style={{ 
          userSelect: 'none',
          touchAction: showDelete ? 'pan-y' : 'auto'
        }}
      >
        <div className="flex items-start justify-between">
          <div className="flex-1">
            <div className="flex items-center gap-2 mb-1">
              <h3 className="text-sm font-medium">{abbreviateStreetName(street.name)}</h3>
              {usersInStreet.length > 0 && (
                <PresenceIndicator users={usersInStreet} size="sm" />
              )}
              {onEdit && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={(e) => {
                    e.stopPropagation();
                    onEdit();
                  }}
                  className="h-6 w-6 p-0 opacity-60 hover:opacity-100"
                >
                  <Edit className="h-3 w-3" />
                </Button>
              )}
            </div>
            <p className="text-sm text-muted-foreground mb-2">
              {street.plz}
            </p>
            
            <div className="flex flex-wrap gap-1">
              {(street.tag_counts?.BK || 0) > 0 && (
                <Badge variant="default" className="text-xs bg-green-100 text-green-800">
                  {street.tag_counts?.BK || 0} BK
                </Badge>
              )}
              {(street.tag_counts?.NA || 0) > 0 && (
                <Badge variant="secondary" className="text-xs">
                  {street.tag_counts?.NA || 0} NA
                </Badge>
              )}
              {(street.tag_counts?.HOT || 0) > 0 && (
                <Badge variant="destructive" className="text-xs">
                  🔥 {street.tag_counts?.HOT || 0}
                </Badge>
              )}
              {(street.tag_counts?.KT || 0) > 0 && (
                <Badge variant="default" className="text-xs bg-blue-100 text-blue-800">
                  {street.tag_counts?.KT || 0} KT
                </Badge>
              )}
              {(street.tag_counts?.KI || 0) > 0 && (
                <Badge variant="outline" className="text-xs">
                  {street.tag_counts?.KI || 0} KI
                </Badge>
              )}
              {(street.tag_counts?.NO || 0) > 0 && (
                <Badge variant="destructive" className="text-xs bg-red-100 text-red-800">
                  {street.tag_counts?.NO || 0} NO
                </Badge>
              )}
              {(street.tag_counts?.WIN || 0) > 0 && (
                <Badge variant="default" className="text-xs bg-yellow-100 text-yellow-800">
                  {street.tag_counts?.WIN || 0} WIN
                </Badge>
              )}
            </div>
          </div>
          
          <div className="flex flex-col items-end gap-2 ml-4">
            {(street.total_units || 0) > 0 && (
              <div className="text-xs text-muted-foreground font-medium">
                {street.total_units} Einheiten
              </div>
            )}
            <MapPin className="h-4 w-4 text-muted-foreground" />
          </div>
        </div>
      </Card>
    </div>
  );
};

// Utility function to abbreviate street names
const abbreviateStreetName = (name: string): string => {
  return name.replace(/straße$/i, 'Str.');
};

export const D2DStreetList = ({ 
  streets, 
  loading, 
  lastPlz, 
  area,
  onSelectStreet, 
  onCreateStreet,
  onDeleteStreet,
  onEditStreetName,
  onBack,
  getUsersInStreet
}: D2DStreetListProps) => {
  const { toast } = useToast();
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [streetName, setStreetName] = useState('');
  const [suggestionSelected, setSuggestionSelected] = useState(false);
  const [plz, setPlz] = useState(() => {
    try {
      // First try to restore from session storage
      const saved = sessionStorage.getItem(STREET_LIST_STATE_KEY);
      if (saved) {
        const parsed = JSON.parse(saved);
        if (parsed.plz) return parsed.plz;
      }
    } catch (error) {
      console.error('Error loading PLZ state:', error);
    }
    return lastPlz || '';
  });
  const [creating, setCreating] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [streetToDelete, setStreetToDelete] = useState<D2DStreet | null>(null);
  const [editingStreetId, setEditingStreetId] = useState<string | null>(null);
  const [editingName, setEditingName] = useState('');
  const [updatingName, setUpdatingName] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  
  // Auto-complete states
  const [streetSuggestions, setStreetSuggestions] = useState<StreetSuggestion[]>([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [isSearching, setIsSearching] = useState(false);
  const [selectedSuggestionIndex, setSelectedSuggestionIndex] = useState(-1);
  const searchTimeoutRef = useRef<NodeJS.Timeout>();
  const inputRef = useRef<HTMLInputElement>(null);
  const suggestionsRef = useRef<HTMLDivElement>(null);

  // Save state whenever it changes
  useEffect(() => {
    const state = {
      plz,
      streetName
    };
    sessionStorage.setItem(STREET_LIST_STATE_KEY, JSON.stringify(state));
  }, [plz, streetName]);

  // Save and restore scroll position
  useEffect(() => {
    const saveScrollPosition = () => {
      if (scrollContainerRef.current) {
        const scrollTop = scrollContainerRef.current.scrollTop;
        sessionStorage.setItem(STREET_LIST_SCROLL_KEY, scrollTop.toString());
      }
    };

    const handleBeforeUnload = () => saveScrollPosition();
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'hidden') saveScrollPosition();
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      saveScrollPosition(); // Save on unmount
    };
  }, []);

  // Restore scroll position after content loads
  useEffect(() => {
    if (streets.length > 0) {
      setTimeout(() => {
        const savedScrollPosition = sessionStorage.getItem(STREET_LIST_SCROLL_KEY);
        if (savedScrollPosition && scrollContainerRef.current) {
          const scrollTop = parseInt(savedScrollPosition, 10);
          scrollContainerRef.current.scrollTop = scrollTop;
          console.log('🔄 Street List: Restored scroll position:', scrollTop);
        }
      }, 100);
    }
  }, [streets.length]);

  // Update PLZ when lastPlz prop changes or area changes
  useEffect(() => {
    setPlz(area?.plz || lastPlz || '');
  }, [lastPlz, area?.plz]);

  // Load top streets for PLZ when focused (no input needed)
  const loadTopStreetsForPlz = async () => {
    const searchPlz = area?.plz || plz || lastPlz;
    
    if (!searchPlz) {
      console.warn('⚠️ No PLZ available for street loading');
      return;
    }

    setIsSearching(true);
    try {
      // Use Photon with just PLZ to get top streets
      const url = `https://photon.komoot.io/api/?q=${encodeURIComponent(searchPlz)}&limit=50&lang=de&osm_tag=highway`;
      
      console.log('🔍 Loading streets for PLZ:', searchPlz);
      
      const response = await fetch(url);
      if (!response.ok) throw new Error('Photon load failed');
      
      const data = await response.json();
      console.log('📍 Photon returned:', data.features?.length || 0, 'results');
      
      if (!data.features) {
        setStreetSuggestions([]);
        setShowSuggestions(false);
        setIsSearching(false);
        return;
      }
      
      const suggestions: StreetSuggestion[] = data.features
        .filter((feature: any) => {
          const props = feature.properties;
          if (!props.name) return false;
          // Allow exact match OR same PLZ prefix (first 3 digits) for city-states like Berlin
          const plzMatch = props.postcode === searchPlz || 
            (props.postcode && searchPlz && props.postcode.substring(0, 3) === searchPlz.substring(0, 3));
          if (!plzMatch) return false;
          return props.osm_key === 'highway' || props.street;
        })
        .map((feature: any) => {
          const props = feature.properties;
          return {
            display_name: `${props.name}, ${searchPlz} ${props.city || ''}`,
            name: props.name,
            postcode: searchPlz,
            city: props.city,
            state: props.state
          };
        })
        .filter((s, i, arr) => arr.findIndex(x => x.name === s.name) === i)
        .sort((a, b) => a.name.localeCompare(b.name))
        .slice(0, 50);

      console.log('✅ Loaded:', suggestions.length, 'top streets');
      setStreetSuggestions(suggestions);
      setShowSuggestions(suggestions.length > 0);
      setSelectedSuggestionIndex(-1);
    } catch (error) {
      console.error('❌ Load streets error:', error);
      setStreetSuggestions([]);
      setShowSuggestions(false);
    } finally {
      setIsSearching(false);
    }
  };

  // Search for street suggestions with user input using Photon API
  const searchStreets = async (query: string) => {
    if (!query || query.length < 1) {
      loadTopStreetsForPlz();
      return;
    }

    setIsSearching(true);
    try {
      const searchPlz = area?.plz || plz || lastPlz;
      
      if (!searchPlz) {
        console.warn('⚠️ No PLZ available for street search');
        setStreetSuggestions([]);
        setShowSuggestions(false);
        setIsSearching(false);
        return;
      }
      
      // Combine PLZ + street name in q parameter for better results
      const combinedQuery = `${searchPlz} ${query}`;
      const url = `https://photon.komoot.io/api/?q=${encodeURIComponent(combinedQuery)}&limit=50&lang=de&osm_tag=highway`;
      
      console.log('🔍 Photon search:', combinedQuery);
      
      const response = await fetch(url);
      if (!response.ok) throw new Error('Photon search failed');
      
      const data = await response.json();
      console.log('📍 Photon returned:', data.features?.length || 0, 'results');
      
      if (!data.features) {
        setStreetSuggestions([]);
        setShowSuggestions(false);
        setIsSearching(false);
        return;
      }
      
      const suggestions: StreetSuggestion[] = data.features
        .filter((feature: any) => {
          const props = feature.properties;
          if (!props.name) return false;
          // Allow exact match OR same PLZ prefix (first 3 digits) for city-states like Berlin
          const plzMatch = props.postcode === searchPlz || 
            (props.postcode && searchPlz && props.postcode.substring(0, 3) === searchPlz.substring(0, 3));
          if (!plzMatch) return false;
          return props.osm_key === 'highway' || props.street;
        })
        .map((feature: any) => {
          const props = feature.properties;
          return {
            display_name: `${props.name}, ${searchPlz} ${props.city || ''}`,
            name: props.name,
            postcode: searchPlz,
            city: props.city,
            state: props.state
          };
        })
        .filter((s, i, arr) => arr.findIndex(x => x.name === s.name) === i)
        .sort((a, b) => {
          const lowerQuery = query.toLowerCase();
          const aStarts = a.name.toLowerCase().startsWith(lowerQuery);
          const bStarts = b.name.toLowerCase().startsWith(lowerQuery);
          if (aStarts && !bStarts) return -1;
          if (!aStarts && bStarts) return 1;
          return a.name.localeCompare(b.name);
        })
        .slice(0, 30);

      console.log('✅ Showing:', suggestions.length, 'suggestions');
      setStreetSuggestions(suggestions);
      setShowSuggestions(suggestions.length > 0);
      setSelectedSuggestionIndex(-1);
    } catch (error) {
      console.error('❌ Photon search error:', error);
      setStreetSuggestions([]);
      setShowSuggestions(false);
    } finally {
      setIsSearching(false);
    }
  };

  // Debounced search handler
  const handleStreetNameChange = (value: string) => {
    setStreetName(value);
    setSuggestionSelected(false); // Reset when user types manually
    
    // Clear existing timeout
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }
    
    // Search immediately without debounce for fast results
    searchStreets(value);
  };

  // Handle suggestion selection
  const selectSuggestion = (suggestion: StreetSuggestion) => {
    setStreetName(suggestion.name);
    if (suggestion.postcode) {
      setPlz(suggestion.postcode);
    }
    setSuggestionSelected(true); // Mark that a suggestion was selected
    setShowSuggestions(false);
    setStreetSuggestions([]);
    setSelectedSuggestionIndex(-1);
    inputRef.current?.focus();
  };

  // Handle keyboard navigation
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (!showSuggestions || streetSuggestions.length === 0) return;
    
    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setSelectedSuggestionIndex(prev => 
          prev < streetSuggestions.length - 1 ? prev + 1 : prev
        );
        break;
      case 'ArrowUp':
        e.preventDefault();
        setSelectedSuggestionIndex(prev => prev > 0 ? prev - 1 : -1);
        break;
      case 'Enter':
        e.preventDefault();
        if (selectedSuggestionIndex >= 0) {
          selectSuggestion(streetSuggestions[selectedSuggestionIndex]);
        }
        break;
      case 'Escape':
        setShowSuggestions(false);
        setSelectedSuggestionIndex(-1);
        break;
    }
  };

  // Close suggestions when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        suggestionsRef.current && 
        !suggestionsRef.current.contains(event.target as Node) &&
        inputRef.current &&
        !inputRef.current.contains(event.target as Node)
      ) {
        setShowSuggestions(false);
        setSelectedSuggestionIndex(-1);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, []);

  const handleCreate = async () => {
    if (!streetName.trim() || !plz.trim()) return;
    
    setCreating(true);
    try {
      await onCreateStreet({
        name: streetName.trim(),
        plz: plz.trim(),
      });
      setStreetName('');
      setPlz(lastPlz || '');
      setSuggestionSelected(false);
      setShowCreateDialog(false);
    } finally {
      setCreating(false);
    }
  };

  const handleDeleteStreet = async () => {
    if (!streetToDelete || !onDeleteStreet) return;
    
    try {
      await onDeleteStreet(streetToDelete.id);
      toast({
        title: "Erfolg",
        description: `Straße "${streetToDelete.name}" wurde gelöscht.`,
      });
    } catch (error) {
      toast({
        title: "Fehler",
        description: "Straße konnte nicht gelöscht werden.",
        variant: "destructive",
      });
    } finally {
      setDeleteDialogOpen(false);
      setStreetToDelete(null);
    }
  };

  const startEditing = (street: D2DStreet) => {
    setEditingStreetId(street.id);
    setEditingName(street.name);
    setShowEditDialog(true);
  };

  const handleUpdateName = async () => {
    if (!editingStreetId || !editingName.trim() || !onEditStreetName) return;
    
    setUpdatingName(true);
    try {
      await onEditStreetName(editingStreetId, editingName.trim());
      toast({
        title: "Erfolg",
        description: "Straßenname wurde aktualisiert.",
      });
      setEditingStreetId(null);
      setEditingName('');
      setShowEditDialog(false);
    } catch (error) {
      toast({
        title: "Fehler",
        description: "Straßenname konnte nicht aktualisiert werden.",
        variant: "destructive",
      });
    } finally {
      setUpdatingName(false);
    }
  };

  const cancelEditing = () => {
    setEditingStreetId(null);
    setEditingName('');
    setShowEditDialog(false);
  };

  if (loading) {
    return (
      <div className="space-y-4">
        <div className="flex items-center gap-2 mb-6">
          <Button variant="ghost" size="sm" onClick={onBack}>
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <div className="h-6 bg-muted rounded w-32 animate-pulse"></div>
        </div>
        {[1, 2, 3].map(i => (
          <Card key={i} className="p-6 animate-pulse">
            <div className="h-6 bg-muted rounded w-1/2 mb-3"></div>
            <div className="h-4 bg-muted rounded w-1/4"></div>
          </Card>
        ))}
      </div>
    );
  }

  const openMapsForArea = () => {
    if (!area) return;
    
    openNavigationForArea(area.name, area.plz);
  };

  return (
    <div ref={scrollContainerRef} className="space-y-4">
      {area && (
        <div className="flex justify-center mb-4">
          <div 
            className="flex flex-col items-center gap-2 py-4 px-8 cursor-pointer bg-gradient-to-br from-primary/10 via-background to-accent/10 hover:from-primary/15 hover:via-background hover:to-accent/15 rounded-2xl transition-all border-2 border-primary/20 hover:border-primary/40 shadow-lg backdrop-blur-sm hover:shadow-xl group"
            onClick={openMapsForArea}
          >
            <div className="text-center">
              <h2 className="text-xl font-bold bg-gradient-to-r from-primary to-accent bg-clip-text text-transparent">
                {area.name}
              </h2>
              {area.plz && <p className="text-sm font-medium text-muted-foreground">{area.plz}</p>}
            </div>
            <MapIcon className="h-5 w-5 text-primary group-hover:scale-110 transition-transform" />
          </div>
        </div>
      )}
      
      <div className="flex items-center gap-2 mb-6">
        <Button variant="ghost" size="sm" onClick={onBack} className="hover:bg-transparent border border-border rounded-sm">
          <ArrowLeft className="h-4 w-4" />
        </Button>
        <MapPin className="h-5 w-5 text-primary" />
        <h2 className="text-xl font-semibold">Straßen</h2>
      </div>

      {/* Add Street Button */}
      <div className="flex flex-col items-center gap-1 mb-4">
        {area?.is_active === false ? (
          <>
            <Button 
              variant="ghost" 
              size="sm"
              disabled
              className="gap-2 text-muted-foreground font-medium bg-muted/10 backdrop-blur-sm border-2 border-border/50 rounded-full px-5 py-2 opacity-50 cursor-not-allowed"
            >
              <Plus className="h-4 w-4" />
              Neue Straße hinzufügen
            </Button>
            <span className="text-xs text-muted-foreground">Gebiet ist inaktiv</span>
          </>
        ) : (
          <Button 
            variant="ghost" 
            size="sm"
            onClick={() => setShowCreateDialog(true)}
            className="gap-2 text-foreground font-medium hover:bg-muted/10 backdrop-blur-sm border-2 border-border rounded-full px-5 py-2 transition-all duration-200"
          >
            <Plus className="h-4 w-4" />
            Neue Straße hinzufügen
          </Button>
        )}
      </div>

      <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Neue Straße anlegen</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="relative">
              <Label htmlFor="street-name">Straßenname</Label>
              <div className="relative">
                <Input
                  ref={inputRef}
                  id="street-name"
                  value={streetName}
                  onChange={(e) => handleStreetNameChange(e.target.value)}
                  onKeyDown={handleKeyDown}
                  onFocus={() => {
                    // Load streets when field is focused
                    if (!streetName) {
                      loadTopStreetsForPlz();
                    } else if (streetSuggestions.length > 0) {
                      setShowSuggestions(true);
                    }
                  }}
                  placeholder="z.B. Hauptstraße - Klicken für Liste"
                  autoComplete="off"
                />
                {isSearching && (
                  <div className="absolute right-3 top-1/2 -translate-y-1/2">
                    <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
                  </div>
                )}
                
                {/* Suggestions Dropdown */}
                {showSuggestions && streetSuggestions.length > 0 && (
                  <div 
                    ref={suggestionsRef}
                    className="absolute top-full left-0 right-0 z-50 mt-1 bg-popover border border-border rounded-md shadow-lg max-h-48 overflow-y-auto"
                  >
                    {streetSuggestions.map((suggestion, index) => (
                      <div
                        key={index}
                        className={`px-3 py-2 cursor-pointer text-sm transition-colors border-b border-border/30 last:border-b-0 ${
                          index === selectedSuggestionIndex 
                            ? 'bg-accent text-accent-foreground' 
                            : 'hover:bg-accent/50'
                        }`}
                        onClick={() => selectSuggestion(suggestion)}
                        onMouseEnter={() => setSelectedSuggestionIndex(index)}
                      >
                        <div className="font-medium text-foreground">
                          {suggestion.name}
                        </div>
                        <div className="text-xs text-muted-foreground">
                          {suggestion.postcode && `${suggestion.postcode} `}
                          {suggestion.city}
                          {suggestion.state && `, ${suggestion.state}`}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
            <div>
              <Label htmlFor="plz">PLZ</Label>
              <Input
                id="plz"
                value={plz}
                onChange={(e) => setPlz(e.target.value)}
                placeholder="z.B. 12345"
              />
            </div>
            <div className="flex gap-2">
              <Button 
                variant="outline" 
                onClick={() => setShowCreateDialog(false)}
                className="flex-1"
              >
                Abbrechen
              </Button>
              <Button 
                onClick={handleCreate}
                disabled={!suggestionSelected || creating}
                className="flex-1"
              >
                {creating ? 'Erstelle...' : 'Erstellen'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {streets.length === 0 ? null : (
          <div className="space-y-3">
            {streets.map((street) => (
              <SwipeableStreetCard
                key={street.id}
                street={street}
                onSelect={() => onSelectStreet(street)}
                onDelete={onDeleteStreet ? () => {
                  setStreetToDelete(street);
                  setDeleteDialogOpen(true);
                } : undefined}
                onEdit={onEditStreetName ? () => startEditing(street) : undefined}
                showDelete={!!onDeleteStreet}
                isAreaInactive={area?.is_active === false}
                usersInStreet={getUsersInStreet?.(street.id)}
              />
            ))}
          </div>
        )}
        {/* Delete Confirmation Dialog */}
        <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Straße löschen?</AlertDialogTitle>
              <AlertDialogDescription>
                Sind Sie sicher, dass Sie die Straße "{streetToDelete?.name}" und alle dazugehörigen 
                Häuser und Wohneinheiten löschen möchten? Diese Aktion kann nicht rückgängig gemacht werden.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Abbrechen</AlertDialogCancel>
              <AlertDialogAction 
                onClick={handleDeleteStreet}
                className="bg-red-600 hover:bg-red-700"
              >
                <Trash2 className="h-4 w-4 mr-2" />
                Löschen
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>

        {/* Edit Name Dialog */}
        <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Straßenname bearbeiten</DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <div>
                <Label htmlFor="edit-street-name">Neuer Straßenname</Label>
                <Input
                  id="edit-street-name"
                  value={editingName}
                  onChange={(e) => setEditingName(e.target.value)}
                  placeholder="z.B. Hauptstraße"
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') handleUpdateName();
                    if (e.key === 'Escape') cancelEditing();
                  }}
                  autoFocus
                />
              </div>
              <div className="flex gap-2">
                <Button 
                  variant="outline" 
                  onClick={cancelEditing}
                  className="flex-1"
                >
                  Abbrechen
                </Button>
                <Button 
                  onClick={handleUpdateName}
                  disabled={!editingName.trim() || updatingName}
                  className="flex-1"
                >
                  {updatingName ? 'Aktualisiere...' : 'Speichern'}
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
    </div>
  );
};